import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Audio", "Video", "Lighting", "Staging", "Cables"];

export function GearFormDialog({ open, onOpenChange, form, setForm, editItem, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card border-border">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{editItem ? 'Edit Gear' : 'Add Gear'}</DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">Fill in gear details below</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Name</Label><Input data-testid="gear-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-secondary/30 border-border" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Category</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger data-testid="gear-category-select" className="bg-secondary/30 border-border"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Total Quantity</Label><Input data-testid="gear-quantity-input" type="number" inputMode="numeric" pattern="[0-9]*" min={1} value={form.quantity} onFocus={e => e.target.select()} onChange={e => {
              const newQty = parseInt(e.target.value) || 1;
              const cb = form.condition_breakdown;
              if (cb) {
                const oldTotal = (cb.good || 0) + (cb.maintenance || 0) + (cb.damaged || 0);
                if (newQty !== oldTotal) {
                  const diff = newQty - oldTotal;
                  setForm({...form, quantity: newQty, condition_breakdown: { ...cb, good: Math.max(0, (cb.good || 0) + diff) }});
                  return;
                }
              }
              setForm({...form, quantity: newQty});
            }} className="bg-secondary/30 border-border" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Location</Label><Input data-testid="gear-location-input" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="bg-secondary/30 border-border" /></div>
          </div>
          <ConditionBreakdownFields form={form} setForm={setForm} />
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Notes</Label><Input data-testid="gear-notes-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-secondary/30 border-border" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="gear-cancel-btn" variant="secondary" onClick={() => onOpenChange(false)} className="text-xs uppercase tracking-wider">Cancel</Button>
          <Button data-testid="gear-save-btn" onClick={onSave} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider">{editItem ? 'Update' : 'Add'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConditionBreakdownFields({ form, setForm }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Condition Breakdown</Label>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-emerald-400 mb-1 block flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Good</Label>
          <Input
            data-testid="gear-cond-good"
            type="number" min={0} max={form.quantity}
            value={form.condition_breakdown?.good ?? form.quantity}
            onChange={e => {
              const good = Math.max(0, Math.min(form.quantity, parseInt(e.target.value) || 0));
              const cb = form.condition_breakdown || { good: form.quantity, maintenance: 0, damaged: 0 };
              const remaining = form.quantity - good;
              const maint = Math.min(cb.maintenance || 0, remaining);
              const dmg = Math.max(0, remaining - maint);
              let cond = "good";
              if (good < form.quantity / 2) cond = dmg > 0 ? "damaged" : "maintenance";
              setForm({...form, condition_breakdown: { good, maintenance: maint, damaged: dmg }, condition: cond});
            }}
            className="bg-secondary/30 border-border h-9 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs text-amber-400 mb-1 block flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Maintenance</Label>
          <Input
            data-testid="gear-cond-maint"
            type="number" min={0} max={form.quantity}
            value={form.condition_breakdown?.maintenance ?? 0}
            onChange={e => {
              const maint = Math.max(0, Math.min(form.quantity, parseInt(e.target.value) || 0));
              const cb = form.condition_breakdown || { good: form.quantity, maintenance: 0, damaged: 0 };
              const remaining = form.quantity - maint;
              setForm({...form, condition_breakdown: { good: Math.min(cb.good ?? form.quantity, remaining), maintenance: maint, damaged: Math.max(0, remaining - Math.min(cb.good ?? form.quantity, remaining)) }});
            }}
            className="bg-secondary/30 border-border h-9 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs text-red-400 mb-1 block flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-400" />Damaged</Label>
          <Input
            data-testid="gear-cond-damaged"
            type="number" min={0} max={form.quantity}
            value={form.condition_breakdown?.damaged ?? 0}
            onChange={e => {
              const dmg = Math.max(0, Math.min(form.quantity, parseInt(e.target.value) || 0));
              const cb = form.condition_breakdown || { good: form.quantity, maintenance: 0, damaged: 0 };
              const remaining = form.quantity - dmg;
              setForm({...form, condition_breakdown: { good: Math.min(cb.good ?? form.quantity, remaining), maintenance: Math.max(0, remaining - Math.min(cb.good ?? form.quantity, remaining)), damaged: dmg }, condition: dmg > 0 ? "damaged" : "good"});
            }}
            className="bg-secondary/30 border-border h-9 text-sm"
          />
        </div>
      </div>
      {form.condition_breakdown && ((form.condition_breakdown.good || 0) + (form.condition_breakdown.maintenance || 0) + (form.condition_breakdown.damaged || 0)) !== form.quantity && (
        <p className="text-xs text-red-400 mt-1">Breakdown must total {form.quantity} (currently {(form.condition_breakdown.good || 0) + (form.condition_breakdown.maintenance || 0) + (form.condition_breakdown.damaged || 0)})</p>
      )}
    </div>
  );
}
